import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { dateTimeFmt, numberFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, ClipboardList, Truck, Printer, FileText, Check, PackageCheck, FileSignature, Loader2, ChevronsUpDown, Pencil, Ban } from "lucide-react";

const STATUS = {
  open: { label: "Menunggu", cls: "bg-amber-100 text-amber-700" },
  accepted: { label: "Diterima", cls: "bg-sky-100 text-sky-700" },
  ready: { label: "Siap Dijemput", cls: "bg-violet-100 text-violet-700" },
  fulfilled: { label: "Dipenuhi (Transfer)", cls: "bg-blue-100 text-blue-700" },
  received: { label: "Diterima", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Ditolak", cls: "bg-rose-100 text-rose-700" },
};

function ProductCombo({ products, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const sel = products.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate">{sel ? `${sel.name} (stok ${numberFmt(sel.stock_qty)})` : "Pilih produk"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Cari produk..." data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>Produk tidak ditemukan</CommandEmpty>
            <CommandGroup>
              {products.map((p) => (
                <CommandItem key={p.id} value={p.name} onSelect={() => { onChange(p.id); setOpen(false); }} data-testid={`${testid}-opt-${p.id}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">stok {numberFmt(p.stock_qty)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function PurchaseOrders() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const isBranchHead = user.role === "branch_head";
  const isWarehouse = user.role === "warehouse";
  const canManageIncoming = isAdmin || isWarehouse;

  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [internalPOs, setInternalPOs] = useState([]);
  const [supplierPOs, setSupplierPOs] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [lines, setLines] = useState([]);
  const [savingPO, setSavingPO] = useState(false);
  const [busyPoId, setBusyPoId] = useState(null);
  const [savingRecv, setSavingRecv] = useState(false);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";
  const guId = useMemo(() => {
    const gu = branches.find((b) => b.is_main) || branches.find((b) => b.code === "GU") || branches.find((b) => b.name === "Gudang Utama");
    return gu?.id || "";
  }, [branches]);

  const prodBranch = isAdmin ? guId : null;

  const load = () => {
    if (isBranchHead) client.get("/purchase-orders/available-products").then((r) => setProducts(r.data));
    else if (guId) client.get("/products", { params: { branch_id: guId } }).then((r) => setProducts(r.data));
    client.get("/suppliers").then((r) => setSuppliers(r.data));
    client.get("/purchase-orders", { params: { po_type: "internal" } }).then((r) => setInternalPOs(r.data));
    if (isAdmin) client.get("/purchase-orders", { params: { po_type: "supplier" } }).then((r) => setSupplierPOs(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [guId, isBranchHead]);

  const prodUnits = (pid) => products.find((p) => p.id === pid)?.units || [];
  const factorOf = (pid, unitName) => (prodUnits(pid).find((u) => u.name === unitName)?.factor) || 1;
  // Batas stok gudang hanya berlaku utk PO internal (cabang -> gudang utama), bukan PO supplier.
  const stockErrors = useMemo(() => {
    if (isAdmin) return {};
    const need = {};
    lines.forEach((l) => {
      if (l.product_id && l.unit) need[l.product_id] = (need[l.product_id] || 0) + (Number(l.qty) || 0) * factorOf(l.product_id, l.unit);
    });
    const errs = {};
    Object.entries(need).forEach(([pid, base]) => {
      const p = products.find((x) => x.id === pid);
      if (p && base > (p.stock_qty || 0)) errs[pid] = { need: base, avail: p.stock_qty || 0, unit: p.base_unit };
    });
    return errs;
    // eslint-disable-next-line
  }, [lines, products, isAdmin]);
  const hasStockError = Object.keys(stockErrors).length > 0;
  const addLine = () => setLines((l) => [...l, { _k: Math.random().toString(36).slice(2), product_id: "", unit: "", qty: 1 }]);
  const setLine = (i, patch) => setLines((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const delLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (savingPO) return;
    if (!lines.length) { toast.error("Tambah minimal 1 item"); return; }
    if (lines.some((l) => !l.product_id || !l.unit)) { toast.error("Lengkapi produk & satuan tiap baris"); return; }
    if (lines.some((l) => Number(l.qty) <= 0)) { toast.error("Jumlah harus lebih dari 0"); return; }
    if (hasStockError) { toast.error("Jumlah PO melebihi stok Gudang Utama"); return; }
    setSavingPO(true);
    try {
      await client.post("/purchase-orders", {
        supplier_id: isAdmin ? (supplierId || null) : null,
        items: lines.map((l) => ({ product_id: l.product_id, unit: l.unit, qty: Number(l.qty) })),
      });
      toast.success(isAdmin ? "PO Supplier dibuat" : "PO dikirim ke Regional Manager"); setLines([]); setSupplierId(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPO(false); }
  };

  const fulfill = async (po) => {
    if (busyPoId) return;
    setBusyPoId(po.id);
    try {
      await client.post(`/purchase-orders/${po.id}/fulfill`);
      toast.success("PO dipenuhi — transfer dari Gudang Utama dibuat (menunggu konfirmasi cabang)"); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyPoId(null); }
  };

  const stepPO = async (po, step, msg) => {
    if (busyPoId) return;
    setBusyPoId(po.id);
    try {
      await client.post(`/purchase-orders/${po.id}/${step}`);
      toast.success(msg); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyPoId(null); }
  };

  // Bukti penjemputan PO internal (tanda tangan)
  const printPickupProof = (po) => {
    const rows = po.items.map((i, idx) => `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee">${idx + 1}</td><td style="padding:6px 10px;border-bottom:1px solid #eee">${i.name}</td><td style="padding:6px 10px;text-align:right;border-bottom:1px solid #eee">${i.qty} ${i.unit}</td><td style="padding:6px 10px;border-bottom:1px solid #eee">&nbsp;</td></tr>`).join("");
    const w = window.open("", "_blank", "width=800,height=900");
    w.document.write(`<html><head><title>Bukti Penjemputan ${po.number}</title></head><body style="font-family:Arial;padding:32px;color:#0f172a;max-width:720px;margin:0 auto">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:12px">
        <div><div style="font-size:22px;font-weight:bold">TOKO KU</div><div style="font-size:12px;color:#555">Gudang Utama</div></div>
        <div style="text-align:right"><div style="font-size:18px;font-weight:bold">BUKTI PENJEMPUTAN BARANG</div><div style="font-family:monospace;font-size:13px">${po.number}</div><div style="font-size:12px;color:#555">${new Date(po.created_at).toLocaleDateString("id-ID")}</div></div>
      </div>
      <div style="margin:16px 0;font-size:13px"><table><tr><td style="padding-right:24px">Cabang Tujuan: <b>${branchName(po.branch_id)}</b></td><td>Pemohon: <b>${po.user_name || "-"}</b></td></tr></table></div>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <thead><tr style="background:#111;color:#fff"><th style="padding:8px 10px;text-align:left;width:40px">No</th><th style="padding:8px 10px;text-align:left">Produk</th><th style="padding:8px 10px;text-align:right">Jumlah</th><th style="padding:8px 10px;text-align:left;width:120px">Cek Fisik</th></tr></thead>
        <tbody>${rows}</tbody></table>
      <div style="margin-top:48px;display:flex;justify-content:space-between;font-size:13px;text-align:center">
        <div>Penyerah (Gudang Utama)<br/><br/><br/><br/>(__________________)</div>
        <div>Penjemput (Cabang)<br/><br/><br/><br/>(__________________)</div>
      </div>
      <div style="margin-top:28px;font-size:11px;color:#777">* Barang diserahkan setelah ditandatangani kedua pihak. Simpan bukti ini untuk validasi transfer.</div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  // Terima PO Supplier -> pembelian + update stok Gudang Utama
  const [recv, setRecv] = useState(null);
  const [recvItems, setRecvItems] = useState([]);
  const openRecv = (po) => {
    setRecv(po);
    setRecvItems(po.items.map((it) => ({ product_id: it.product_id, name: it.name, unit: it.unit, qty: it.qty, cost_per_unit: 0 })));
  };
  const submitRecv = async () => {
    if (savingRecv) return;
    setSavingRecv(true);
    try {
      await client.post(`/purchase-orders/${recv.id}/receive`, { items: recvItems.map((i) => ({ product_id: i.product_id, unit: i.unit, qty: Number(i.qty), cost_per_unit: Number(i.cost_per_unit) })) });
      toast.success("PO supplier diterima → stok & HPP Gudang Utama ter-update"); setRecv(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingRecv(false); }
  };

  // Edit item PO internal (hanya status Menunggu)
  const [editPo, setEditPo] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const openEdit = (po) => {
    setEditPo(po);
    setEditLines(po.items.map((it) => ({ _k: Math.random().toString(36).slice(2), product_id: it.product_id, unit: it.unit, qty: it.qty })));
  };
  const addEditLine = () => setEditLines((l) => [...l, { _k: Math.random().toString(36).slice(2), product_id: "", unit: "", qty: 1 }]);
  const setEditLine = (i, patch) => setEditLines((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const delEditLine = (i) => setEditLines((l) => l.filter((_, idx) => idx !== i));
  const submitEdit = async () => {
    if (savingEdit) return;
    if (!editLines.length) { toast.error("Tambah minimal 1 item"); return; }
    if (editLines.some((l) => !l.product_id || !l.unit)) { toast.error("Lengkapi produk & satuan tiap baris"); return; }
    if (editLines.some((l) => Number(l.qty) <= 0)) { toast.error("Jumlah harus lebih dari 0"); return; }
    setSavingEdit(true);
    try {
      await client.put(`/purchase-orders/${editPo.id}`, { items: editLines.map((l) => ({ product_id: l.product_id, unit: l.unit, qty: Number(l.qty) })) });
      toast.success("PO berhasil diperbarui"); setEditPo(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingEdit(false); }
  };

  // Tolak PO internal (wajib alasan)
  const [rejectPo, setRejectPo] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [savingReject, setSavingReject] = useState(false);
  const submitReject = async () => {
    if (savingReject) return;
    if (!rejectReason.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
    setSavingReject(true);
    try {
      await client.post(`/purchase-orders/${rejectPo.id}/reject`, { reason: rejectReason.trim() });
      toast.success("PO ditolak"); setRejectPo(null); setRejectReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingReject(false); }
  };

  // Hapus PO internal
  const [delPo, setDelPo] = useState(null);
  const confirmDelete = async () => {
    if (busyPoId) return;
    setBusyPoId(delPo.id);
    try {
      await client.delete(`/purchase-orders/${delPo.id}`);
      toast.success("PO dihapus"); setDelPo(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyPoId(null); }
  };

  // Cetak daftar PO internal yang belum dipenuhi (untuk admin)
  const printInternalList = () => {
    const list = internalPOs.filter((p) => p.status === "open");
    if (!list.length) { toast.error("Tidak ada PO menunggu untuk dicetak"); return; }
    const blocks = list.map((po) => {
      const rows = po.items.map((i) => `<tr><td style="padding:4px 10px;border-bottom:1px solid #f0f0f0">${i.name}</td><td style="padding:4px 10px;text-align:right;border-bottom:1px solid #f0f0f0">${i.qty} ${i.unit}</td></tr>`).join("");
      return `<div style="margin-bottom:14px;border:1px solid #e5e5e5;border-radius:8px;overflow:hidden">
        <div style="background:#f8fafc;padding:8px 12px;display:flex;justify-content:space-between;font-size:13px">
          <div><b>${branchName(po.branch_id)}</b> · <span style="font-family:monospace">${po.number}</span></div>
          <div>Oleh: ${po.user_name || "-"}</div></div>
        <table style="width:100%;border-collapse:collapse;font-size:12px"><tbody>${rows}</tbody></table></div>`;
    }).join("");
    const w = window.open("", "_blank", "width=800,height=900");
    w.document.write(`<html><head><title>Daftar PO Cabang</title></head><body style="font-family:Arial;padding:28px;color:#111;max-width:720px;margin:0 auto">
      <div style="text-align:center;margin-bottom:6px"><div style="font-size:22px;font-weight:bold">TOKO KU</div><div style="font-size:14px;color:#555">Daftar Permintaan Barang (PO) dari Cabang</div></div>
      <div style="text-align:center;font-size:12px;color:#777;margin-bottom:16px">Status: Menunggu · Dicetak ${new Date().toLocaleString("id-ID")} · ${list.length} PO</div>
      ${blocks}
      <div style="text-align:center;font-size:11px;color:#999;margin-top:20px">— TOKO KU —</div></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  // Cetak PDF PO Supplier (untuk dikirim ke supplier)
  const printSupplierPO = (po) => {
    const sup = suppliers.find((s) => s.id === po.supplier_id);
    const rows = po.items.map((i, idx) => `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee">${idx + 1}</td><td style="padding:6px 10px;border-bottom:1px solid #eee">${i.name}</td><td style="padding:6px 10px;text-align:right;border-bottom:1px solid #eee">${i.qty} ${i.unit}</td></tr>`).join("");
    const w = window.open("", "_blank", "width=800,height=900");
    w.document.write(`<html><head><title>PO ${po.number}</title></head><body style="font-family:Arial;padding:32px;color:#0f172a;max-width:720px;margin:0 auto">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:12px">
        <div><div style="font-size:22px;font-weight:bold">TOKO KU</div><div style="font-size:12px;color:#555">Gudang Utama</div></div>
        <div style="text-align:right"><div style="font-size:18px;font-weight:bold">PURCHASE ORDER</div><div style="font-family:monospace;font-size:13px">${po.number}</div><div style="font-size:12px;color:#555">${new Date(po.created_at).toLocaleDateString("id-ID")}</div></div>
      </div>
      <div style="margin:16px 0;font-size:13px">
        <div style="font-weight:bold;margin-bottom:4px">Kepada Supplier:</div>
        <div>${sup ? sup.name : "(Umum)"}</div>${sup && sup.phone ? `<div>${sup.phone}</div>` : ""}${sup && sup.address ? `<div>${sup.address}</div>` : ""}
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <thead><tr style="background:#111;color:#fff"><th style="padding:8px 10px;text-align:left;width:40px">No</th><th style="padding:8px 10px;text-align:left">Produk</th><th style="padding:8px 10px;text-align:right">Jumlah</th></tr></thead>
        <tbody>${rows}</tbody></table>
      ${po.note ? `<div style="margin-top:14px;font-size:12px;color:#555">Catatan: ${po.note}</div>` : ""}
      <div style="margin-top:48px;display:flex;justify-content:space-between;font-size:13px"><div>Hormat kami,<br/><br/><br/>(__________)<br/>Regional Manager</div><div>Diterima supplier,<br/><br/><br/>(__________)</div></div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  const openInternal = internalPOs.filter((p) => p.status === "open");

  return (
    <div className="p-6 space-y-6 animate-in-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Purchase Order</h1>
        <p className="text-sm text-muted-foreground">
          {isWarehouse ? "Kelola permintaan barang (PO) dari cabang di region Anda — terima, siapkan & penuhi via transfer dari Gudang Utama" : isBranchHead ? "Ajukan permintaan barang ke Regional Manager untuk cabang Anda" : "Kelola permintaan barang dari cabang & buat PO ke supplier untuk Gudang Utama"}
        </p>
      </div>

      {/* FORM BUAT PO */}
      {!isWarehouse && (
      <Card className="p-5">
        <h3 className="mb-3 font-heading text-lg font-semibold">{isAdmin ? "Buat PO Supplier (Gudang Utama)" : "Ajukan Permintaan Barang (PO)"}</h3>
        {isAdmin && (
          <div className="mb-3 w-64 space-y-2"><Label>Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Item {isAdmin ? "(stok Gudang Utama)" : "(tersedia di Gudang Utama)"}</Label>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="po-add-line"><Plus className="mr-1 h-3 w-3" /> Tambah</Button>
          </div>
          {lines.map((l, i) => (
            <div key={l._k}>
              <div className="grid grid-cols-12 items-center gap-2" data-testid={`po-line-${i}`}>
                <div className="col-span-7">
                  <ProductCombo products={products} value={l.product_id}
                    onChange={(v) => setLine(i, { product_id: v, unit: products.find((p) => p.id === v)?.base_unit || "" })}
                    testid={`po-product-${i}`} />
                </div>
                <div className="col-span-3">
                  <Select value={l.unit} onValueChange={(v) => setLine(i, { unit: v })}>
                    <SelectTrigger data-testid={`po-unit-${i}`}><SelectValue placeholder="Satuan" /></SelectTrigger>
                    <SelectContent>{prodUnits(l.product_id).map((u) => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className={`col-span-1 font-mono ${stockErrors[l.product_id] ? "border-destructive text-destructive" : ""}`} type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`po-qty-${i}`} />
                <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => delLine(i)} data-testid={`po-del-line-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              {stockErrors[l.product_id] && (
                <div className="mt-1 text-xs font-medium text-destructive" data-testid={`po-stock-error-${i}`}>
                  Melebihi stok Gudang Utama: diminta {numberFmt(stockErrors[l.product_id].need)} {stockErrors[l.product_id].unit}, tersedia {numberFmt(stockErrors[l.product_id].avail)} {stockErrors[l.product_id].unit}
                </div>
              )}
            </div>
          ))}
          {!lines.length && <div className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">Belum ada item.</div>}
        </div>
        <div className="mt-4 flex justify-end border-t pt-4">
          <Button onClick={submit} disabled={savingPO || hasStockError} data-testid="submit-po">
            {savingPO ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardList className="mr-2 h-4 w-4" />}
            {savingPO ? "Menyimpan…" : (isAdmin ? "Buat PO Supplier" : "Kirim PO ke Regional Manager")}
          </Button>
        </div>
      </Card>
      )}

      {/* ADMIN/GUDANG: PO MASUK DARI CABANG */}
      {canManageIncoming && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">PO Masuk dari Cabang</h2>
            <Button variant="outline" onClick={printInternalList} data-testid="print-internal-po"><Printer className="mr-2 h-4 w-4" /> Cetak Daftar ({openInternal.length})</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Cabang</TableHead><TableHead>Oleh</TableHead><TableHead>Waktu</TableHead><TableHead>Item</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {internalPOs.map((po) => (
                  <TableRow key={po.id} className="hover:bg-slate-50" data-testid={`po-internal-${po.id}`}>
                    <TableCell className="font-mono text-xs">{po.number}</TableCell>
                    <TableCell className="text-sm font-medium">{branchName(po.branch_id)}</TableCell>
                    <TableCell className="text-sm">{po.user_name}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(po.created_at)}</TableCell>
                    <TableCell className="text-xs">{po.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</TableCell>
                    <TableCell><Badge className={STATUS[po.status]?.cls}>{STATUS[po.status]?.label}</Badge>{po.transfer_number && <div className="text-[10px] text-muted-foreground font-mono">{po.transfer_number}</div>}{po.status === "rejected" && po.reject_reason && <div className="text-[10px] text-rose-600 max-w-[160px]">Alasan: {po.reject_reason}</div>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {po.status === "open" && <Button size="sm" onClick={() => stepPO(po, "accept", "PO diterima")} disabled={busyPoId === po.id} data-testid={`accept-${po.id}`}>{busyPoId === po.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />} Terima</Button>}
                        {po.status === "open" && <Button size="sm" variant="outline" onClick={() => openEdit(po)} data-testid={`edit-${po.id}`}><Pencil className="mr-1 h-3 w-3" /> Ubah</Button>}
                        {po.status === "open" && <Button size="sm" variant="outline" className="text-amber-700 hover:text-amber-800" onClick={() => { setRejectPo(po); setRejectReason(""); }} data-testid={`reject-${po.id}`}><Ban className="mr-1 h-3 w-3" /> Tolak</Button>}
                        {po.status === "open" && <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDelPo(po)} data-testid={`delete-${po.id}`}><Trash2 className="mr-1 h-3 w-3" /> Hapus</Button>}
                        {(po.status === "accepted" || po.status === "ready") && <Button size="sm" variant="outline" onClick={() => printPickupProof(po)} data-testid={`proof-${po.id}`}><FileSignature className="mr-1 h-3 w-3" /> Bukti</Button>}
                        {po.status === "accepted" && <Button size="sm" variant="outline" onClick={() => stepPO(po, "ready", "Ditandai siap dijemput di Gudang Utama")} disabled={busyPoId === po.id} data-testid={`ready-${po.id}`}>{busyPoId === po.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <PackageCheck className="mr-1 h-3 w-3" />} Siap Dijemput</Button>}
                        {po.status === "ready" && <Button size="sm" onClick={() => fulfill(po)} disabled={busyPoId === po.id} data-testid={`fulfill-${po.id}`}>{busyPoId === po.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Truck className="mr-1 h-3 w-3" />} {busyPoId === po.id ? "Memproses…" : "Penuhi (Transfer)"}</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!internalPOs.length && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Belum ada PO dari cabang</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ADMIN: RIWAYAT PO SUPPLIER */}
      {isAdmin && (
        <div>
          <h2 className="mb-3 font-heading text-xl font-semibold">PO Supplier (Gudang Utama)</h2>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Supplier</TableHead><TableHead>Waktu</TableHead><TableHead>Item</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {supplierPOs.map((po) => (
                  <TableRow key={po.id} className="hover:bg-slate-50" data-testid={`po-supplier-${po.id}`}>
                    <TableCell className="font-mono text-xs">{po.number}</TableCell>
                    <TableCell className="text-sm">{suppliers.find((s) => s.id === po.supplier_id)?.name || "(Umum)"}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(po.created_at)}</TableCell>
                    <TableCell className="text-xs">{po.items.length} item</TableCell>
                    <TableCell><Badge className={STATUS[po.status]?.cls}>{STATUS[po.status]?.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      {po.status !== "received" && <Button size="sm" className="mr-1" onClick={() => openRecv(po)} data-testid={`po-receive-${po.id}`}><PackageCheck className="mr-1 h-3 w-3" /> Terima</Button>}
                      <Button size="sm" variant="outline" onClick={() => printSupplierPO(po)} data-testid={`po-pdf-${po.id}`}><FileText className="mr-1 h-3 w-3" /> PDF</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!supplierPOs.length && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Belum ada PO supplier</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* KEPALA CABANG: RIWAYAT PO SENDIRI */}
      {isBranchHead && (
        <div>
          <h2 className="mb-3 font-heading text-xl font-semibold">Riwayat PO Saya</h2>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Item</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {internalPOs.map((po) => (
                  <TableRow key={po.id} className="hover:bg-slate-50" data-testid={`po-row-${po.id}`}>
                    <TableCell className="font-mono text-xs">{po.number}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(po.created_at)}</TableCell>
                    <TableCell className="text-xs">{po.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</TableCell>
                    <TableCell><Badge className={STATUS[po.status]?.cls}>{STATUS[po.status]?.label}</Badge>{po.transfer_number && <div className="text-[10px] text-muted-foreground font-mono">{po.transfer_number}</div>}{po.status === "rejected" && po.reject_reason && <div className="text-[10px] text-rose-600">Alasan: {po.reject_reason}</div>}</TableCell>
                  </TableRow>
                ))}
                {!internalPOs.length && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Belum ada PO</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={!!recv} onOpenChange={(o) => !o && setRecv(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Terima PO {recv?.number}</DialogTitle><DialogDescription>Isi harga beli per satuan; stok & HPP Gudang Utama akan diperbarui.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {recvItems.map((it, i) => (
              <div key={it.product_id} className="flex items-center gap-2 rounded-md border p-2">
                <div className="flex-1 text-sm font-medium">{it.name}</div>
                <Input type="number" value={it.qty} onChange={(e) => setRecvItems((s) => s.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} className="w-20 font-mono" data-testid={`recv-qty-${i}`} />
                <span className="text-xs text-muted-foreground">{it.unit}</span>
                <Input type="number" placeholder="harga beli" value={it.cost_per_unit} onChange={(e) => setRecvItems((s) => s.map((x, idx) => idx === i ? { ...x, cost_per_unit: e.target.value } : x))} className="w-32 font-mono" data-testid={`recv-cost-${i}`} />
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRecv(null)}>Batal</Button><Button onClick={submitRecv} disabled={savingRecv} data-testid="submit-recv">{savingRecv ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memproses…</> : "Terima & Catat Pembelian"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT PO INTERNAL */}
      <Dialog open={!!editPo} onOpenChange={(o) => !o && setEditPo(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Ubah PO {editPo?.number}</DialogTitle><DialogDescription>Ubah item permintaan barang. Hanya bisa saat status masih Menunggu.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Item</Label>
              <Button size="sm" variant="outline" onClick={addEditLine} data-testid="edit-po-add-line"><Plus className="mr-1 h-3 w-3" /> Tambah</Button>
            </div>
            {editLines.map((l, i) => (
              <div key={l._k} className="grid grid-cols-12 items-center gap-2" data-testid={`edit-po-line-${i}`}>
                <div className="col-span-6">
                  <ProductCombo products={products} value={l.product_id}
                    onChange={(v) => setEditLine(i, { product_id: v, unit: products.find((p) => p.id === v)?.base_unit || "" })}
                    testid={`edit-po-product-${i}`} />
                </div>
                <div className="col-span-3">
                  <Select value={l.unit} onValueChange={(v) => setEditLine(i, { unit: v })}>
                    <SelectTrigger data-testid={`edit-po-unit-${i}`}><SelectValue placeholder="Satuan" /></SelectTrigger>
                    <SelectContent>{prodUnits(l.product_id).map((u) => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-2 font-mono" type="number" value={l.qty} onChange={(e) => setEditLine(i, { qty: e.target.value })} data-testid={`edit-po-qty-${i}`} />
                <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => delEditLine(i)} data-testid={`edit-po-del-line-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {!editLines.length && <div className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">Belum ada item.</div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditPo(null)}>Batal</Button><Button onClick={submitEdit} disabled={savingEdit} data-testid="submit-edit-po">{savingEdit ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyimpan…</> : "Simpan Perubahan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TOLAK PO INTERNAL */}
      <Dialog open={!!rejectPo} onOpenChange={(o) => !o && setRejectPo(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Tolak PO {rejectPo?.number}</DialogTitle><DialogDescription>PO dari cabang {branchName(rejectPo?.branch_id)} akan ditolak. Alasan ditampilkan ke cabang.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Alasan Penolakan</Label>
            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Contoh: stok gudang tidak mencukupi" data-testid="reject-reason-input" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRejectPo(null)}>Batal</Button><Button className="bg-amber-600 hover:bg-amber-700" onClick={submitReject} disabled={savingReject} data-testid="submit-reject-po">{savingReject ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memproses…</> : "Tolak PO"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HAPUS PO INTERNAL */}
      <Dialog open={!!delPo} onOpenChange={(o) => !o && setDelPo(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Hapus PO {delPo?.number}?</DialogTitle><DialogDescription>Permintaan barang dari cabang {branchName(delPo?.branch_id)} akan dihapus permanen. Tindakan ini tidak bisa dibatalkan.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDelPo(null)}>Batal</Button><Button variant="destructive" onClick={confirmDelete} disabled={busyPoId === delPo?.id} data-testid="confirm-delete-po">{busyPoId === delPo?.id ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menghapus…</> : "Hapus PO"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
